import { z, iso } from "zod";

/** VN mobile: leading +84 or 0, then 9–10 digits (total length 10–12). */
export const VN_PHONE_REGEX = /^(\+84|0)[0-9]{9,10}$/;

export const phoneSchema = z
  .string()
  .min(1)
  .regex(VN_PHONE_REGEX, "Invalid VN phone");

export const uuidSchema = z.string().uuid();

export const isoDateTimeString = iso.datetime({ offset: true });

export const isoDateString = iso.date();

export const otpCodeSchema = z
  .string()
  .regex(/^[0-9]{6}$/, "OTP must be 6 digits");

export const emailSchema = z.string().email().max(254);

/** BigInt VND money. Accept stringified bigint or number, coerce to bigint. */
export const vndAmountSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .transform((v, ctx) => {
    try {
      const big = typeof v === "bigint" ? v : BigInt(v);
      if (big < BigInt(0)) {
        ctx.addIssue({
          code: "custom",
          message: "VND amount cannot be negative",
        });
        return z.NEVER;
      }
      return big;
    } catch {
      ctx.addIssue({ code: "custom", message: "Invalid VND amount" });
      return z.NEVER;
    }
  });

/** Positive integer VND (used for posting wage range, fits in Postgres INT). */
export const vndIntSchema = z.coerce.number().int().min(0).max(2_000_000_000);

export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/**
 * Page-number pagination (web admin/employer tables). Distinct from the
 * cursor-based `paginationQuerySchema` used by mobile infinite-scroll feeds.
 * `page` is 1-based; `limit` allows the 20/50/100 page-size options.
 */
export const pagePaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export type PagePaginationQuery = z.infer<typeof pagePaginationQuerySchema>;

/** Wraps an item schema into a page-number list response with a total count. */
export function pagedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number().int(),
  });
}

/** Lat/Lng for VN. */
export const latitudeSchema = z.coerce.number().min(8).max(24);
export const longitudeSchema = z.coerce.number().min(102).max(110);
